import { DiagnosticsSnapshot, StreamMetadata, StreamingError } from '../types';

const MAGIC = 0xCAFE;
const VERSION = 0x01;

enum PacketType {
  Audio = 0x00,
  Control = 0x01,
  Metadata = 0x02,
  Heartbeat = 0x03,
  Ack = 0x04,
  Error = 0x05,
}

interface PacketHeader {
  magic: number;
  version: number;
  type: PacketType;
  sequence: number;
  length: number;
}

export interface StreamingSubscriberOptions {
  serverUrl: string;
  onMetadata?: (metadata: StreamMetadata, rawPayload: string) => void;
  onAudioFrame?: (frame: Uint8Array) => void;
  onControl?: (data: Record<string, unknown>) => void;
  onHeartbeat?: () => void;
  onError?: (error: StreamingError) => void;
  onClose?: (event?: CloseEvent) => void;
  onConnectionChange?: (state: 'connecting' | 'connected' | 'closed' | 'error') => void;
}

export class StreamingSubscriber {
  private ws: WebSocket | null = null;
  private readonly options: StreamingSubscriberOptions;
  private stats: DiagnosticsSnapshot = {
    packets: 0,
    audioFrames: 0,
    bytes: 0,
    lastSequence: 0,
    startedAt: Date.now(),
    lastUpdated: Date.now(),
    reconnects: 0,
  };

  constructor(options: StreamingSubscriberOptions) {
    this.options = options;
  }

  connect(): Promise<void> {
    return new Promise((resolve, reject) => {
      this.cleanup();
      this.options.onConnectionChange?.('connecting');

      try {
        this.ws = new WebSocket(this.options.serverUrl);
      } catch (error) {
        const err = error instanceof Error ? error.message : String(error);
        this.options.onError?.({ message: err });
        this.options.onConnectionChange?.('error');
        reject(error);
        return;
      }

      this.ws.binaryType = 'arraybuffer';

      this.ws.onopen = () => {
        this.options.onConnectionChange?.('connected');
        resolve();
      };

      this.ws.onerror = (event) => {
        const message =
          event instanceof ErrorEvent ? event.message : 'Unknown WebSocket error occurred';
        this.options.onError?.({ message });
        this.options.onConnectionChange?.('error');
      };

      this.ws.onmessage = (event) => {
        if (event.data instanceof ArrayBuffer) {
          this.handleMessage(new Uint8Array(event.data));
        } else if (event.data instanceof Blob) {
          event.data.arrayBuffer().then((buffer) => this.handleMessage(new Uint8Array(buffer)));
        }
      };

      this.ws.onclose = (event) => {
        this.options.onClose?.(event);
        this.options.onConnectionChange?.('closed');
      };
    });
  }

  private handleMessage(data: Uint8Array): void {
    let offset = 0;
    
    // Handle multiple packets in a single WebSocket message
    while (offset < data.length) {
      if (data.length - offset < 8) {
        // Not enough data for a header
        break;
      }

      const header = this.parseHeader(data.slice(offset));
      if (header.magic !== MAGIC || header.version !== VERSION) {
        // Invalid packet, skip
        break;
      }

      const expectedPacketSize = 8 + header.length;
      if (data.length - offset < expectedPacketSize) {
        // Packet not complete yet
        break;
      }

      const payload = data.slice(offset + 8, offset + 8 + header.length);
      this.stats.packets += 1;
      this.stats.bytes += payload.length;
      this.stats.lastSequence = header.sequence;
      this.stats.lastUpdated = Date.now();

      switch (header.type) {
        case PacketType.Metadata:
          this.handleMetadata(payload);
          break;
        case PacketType.Audio:
          this.stats.audioFrames += 1;
          this.options.onAudioFrame?.(payload);
          break;
        case PacketType.Control:
          this.handleControl(payload);
          break;
        case PacketType.Heartbeat:
          this.options.onHeartbeat?.();
          break;
        case PacketType.Error:
          this.handleError(payload);
          break;
        default:
          break;
      }

      offset += expectedPacketSize;
    }
  }

  private handleMetadata(payload: Uint8Array): void {
    try {
      const json = new TextDecoder().decode(payload);
      const metadata = JSON.parse(json) as StreamMetadata;
      this.options.onMetadata?.(metadata, json);
    } catch (error) {
      this.options.onError?.({
        message: `Failed to parse metadata: ${error instanceof Error ? error.message : String(error)}`,
      });
    }
  }

  private handleControl(payload: Uint8Array): void {
    try {
      const json = new TextDecoder().decode(payload);
      const data = JSON.parse(json) as Record<string, unknown>;
      this.options.onControl?.(data);
    } catch (error) {
      this.options.onError?.({
        message: `Failed to parse control packet: ${
          error instanceof Error ? error.message : String(error)
        }`,
      });
    }
  }

  private handleError(payload: Uint8Array): void {
    try {
      const json = new TextDecoder().decode(payload);
      const data = JSON.parse(json) as StreamingError;
      this.stats.lastError = data.message;
      this.options.onError?.(data);
    } catch (error) {
      this.options.onError?.({
        message: `Server error packet (failed to parse): ${
          error instanceof Error ? error.message : String(error)
        }`,
      });
    }
  }

  private parseHeader(data: Uint8Array): PacketHeader {
    const view = new DataView(data.buffer, data.byteOffset, data.byteLength);
    return {
      magic: view.getUint16(0, true),
      version: data[2],
      type: data[3] as PacketType,
      sequence: view.getUint16(4, true),
      length: view.getUint16(6, true),
    };
  }

  getDiagnostics(): DiagnosticsSnapshot {
    return { ...this.stats };
  }

  cleanup(): void {
    if (this.ws) {
      this.ws.onclose = null;
      this.ws.close();
      this.ws = null;
    }
  }
}

